<template>
  <PageWrapper
    :active-page="PAGE_COMMUNITY_COMMUNITY"
    :active-section="SECTION_COMMUNITY"
    :breadcrumbs="breadcrumbs"
    :page-name="publication.name"
    align="left"
    sub-title=""
    width="full"
  >
    <iColumn gap="standard">
      <ProfileWrapper
        :logo-image="publicationLogo"
        :splash-image="publicationSplashImage"
        :title="publication.name"
      >
        <template #details>
          <iColumn gap="extraSmall" width="hug">
            <iLink :href="domainUrl">
              {{ domainUrl }}
            </iLink>
            <iText class="description">
              {{ publication.description }}
            </iText>
          </iColumn>
        </template>
        <template #menu>
          <iRow
            wrap="nowrap"
            width="hug"
            height="fill"
            vertical-align="middle"
          >
            <iButton v-if="!isUserSubscribed" variant="secondary" @click="goToSubscribeNowPage">
              {{ subscriptionButtonText }}
            </iButton>
            <iLink v-else variant="subtle" :href="`${domainUrl}/subscriptions`">
              <iButton variant="secondary">
                Manage Subscription
              </iButton>
            </iLink>
            <iDropdownMenu
              :items="dropdownItems"
              :show-dropdown="isDropdownOpen"
              :teleport-to-body="false"
              position="right"
              @close="closeDropdown"
              @click="updateStatus"
            >
              <template #trigger>
                <iIcon icon="dots-vertical" @click="openDropdown" />
              </template>
            </iDropdownMenu>
          </iRow>
        </template>
        <iSpace :height="1" />
        <iRow wrap="nowrap" width="fill" overflow="hidden">
          <iColumn gap="large">
            <iRow wrap="wrap" class="card-container" align="left">
              <iColumn gap="extraSmall" width="hug" :max-width="240">
                <iSubHeading>Subscribers</iSubHeading>
                <iText font-size="displaySmall" style="line-height: 1.0">
                  {{ publication.metrics.find(metric => metric.name === 'Subscribers').value }}
                </iText>
              </iColumn>
              <iColumn gap="extraSmall" width="hug" :max-width="240">
                <ApplyToCollaborateProfile
                  :publication="publication"
                  :collaborator-relationship="collaboratorRelationship"
                />
              </iColumn>
              <iColumn gap="extraSmall" width="hug" :max-width="240">
                <iSubHeading>Networks</iSubHeading>
                <SmartFeedV2
                  :source="usePublicationNetworksFeedStore"
                  default-feed-layout="carousel"
                >
                  <template #empty-state>
                    <iText variant="subtle">
                      This publication is not a part of any networks
                    </iText>
                  </template>
                </SmartFeedV2>
              </iColumn>
              <iColumn gap="extraSmall" width="hug" :max-width="240">
                <iSubHeading>
                  Refer & Earn
                </iSubHeading>
                <PublicationReferralLink />
              </iColumn>
              <iColumn gap="extraSmall" width="hug" :max-width="240">
                <iSubHeading>Team</iSubHeading>
                <SmartFeed
                  v-if="hasCollaborators"
                  :pinia-store="usePublicationCollaboratorFeedStore"
                  default-smart-feed-layout="grid"
                  default-entity-layout="cardSmall"
                />
                <PublicationCollaboratorCardSmall
                  :entity="{
                    name: publication.owner.name,
                    role: 'owner',
                    status: '',
                  }"
                />
              </iColumn>
            </iRow>
          </iColumn>
          <iColumn v-if="useCommunityPublicationStoryFeedStore().feed.length > 0" :width="600" class="story-card-section">
            <iColumn gap="extraSmall" :width="600" class="team-section">
              <iSubHeading>Recent Stories</iSubHeading>
              <SmartFeed
                default-smart-feed-layout="grid"
                default-entity-layout="cardSmall"
                :pinia-store="useCommunityPublicationStoryFeedStore"
              >
                <template #empty-state>
                  <iCard width="fill" :style-overrides="{borderWidth: 'none'}">
                    <template #body>
                      <iColumn gap="large" align="center">
                        <iColumn gap="standard" align="center">
                          <iSubHeading variant="subtle">
                            No stories for this publication yet
                          </iSubHeading>
                          <iParagraph
                            variant="subtle"
                            text-align="center"
                            :max-width="600"
                          >
                            Stories for this publication will appear here.
                          </iParagraph>
                        </iColumn>
                      </iColumn>
                    </template>
                  </iCard>
                </template>
              </SmartFeed>
            </iColumn>
          </iColumn>
        </iRow>
      </ProfileWrapper>
    </iColumn>
  </PageWrapper>
  <InvitePublicationModal />
</template>

<script>
import PageWrapper from "@/components/PageWrapper";
import {
  PAGE_AFFILIATE_PLATFORM_SETTINGS,
  SECTION_COMMUNITY,
  PAGE_COMMUNITY_COMMUNITY,
} from "@/constants/nav-constants";
import { mapState, mapActions } from "pinia";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";
import ProfileWrapper from "@/components/publication/ProfileWrapper";
import SmartFeed from "SmartFeed/components/SmartFeed";
import SmartFeedV2 from "SmartFeed/components/SmartFeedV2";
import { usePublicationCollaboratorFeedStore } from "SmartFeed/stores/feed/publication-collaborator-feed-store";
import { useOrganizationStore } from "@/stores/organization-store";
import ApplyToCollaborateProfile from "@/components/publication/ApplyToCollaborateProfile.vue";
import { useCommunityPublicationStoryFeedStore } from "SmartFeed/stores/feed/community-publication-story-feed-store";
import { useNetworkStore } from "@/stores/network-store";
import InvitePublicationModal from "@/components/networks/InvitePublicationModal.vue";
import PublicationCollaboratorCardSmall
  from "SmartFeed/components/publication/layouts/PublicationCollaboratorCardSmall.vue";
import { MenuConstants } from "@bloglovin/vue-component-library";
import PublicationReferralLink from "@/components/publication/PublicationReferralLink.vue";
import { usePublicationNetworksFeedStore } from "@/smart-feed/stores/feed/publication-networks-feed-store";

export default {
  name: "PublicationProfile",
  components: {
    PublicationReferralLink,
    PublicationCollaboratorCardSmall,
    ApplyToCollaborateProfile,
    ProfileWrapper,
    PageWrapper,
    SmartFeed,
    SmartFeedV2,
    InvitePublicationModal,
  },
  data() {
    return {
      SECTION_COMMUNITY,
      PAGE_AFFILIATE_PLATFORM_SETTINGS,
      PAGE_COMMUNITY_COMMUNITY,
      activeTab: "profile",
      tabs: [
        { id: "profile", label: "Profile" },
        { id: "stories", label: "Stories" },
      ],
      isDropdownOpen: false,
    };
  },
  computed: {
    ...mapState(useCreatorPublicationStore, [
      "publication",
      "publicationId",
      "publicationSplashImage",
      "publicationLogo",
      "subscription",
    ]),
    ...mapState(useOrganizationStore, ["getOrgData"]),
    ...mapState(useCreatorPublicationStore, ["collaboratorRelationship"]),
    ...mapState(useNetworkStore, ["ownedNetworks"]),
    isUserSubscribed() {
      return this.subscription.length === 0;
    },
    subscriptionButtonText() {
      if (this.isUserSubscribed) {
        return "Subscribed";
      }
      return "Subscribe";
    },
    isOwner() {
      return this.getOrgData.org_id === this.publication.org_id;
    },
    hasCollaborators() {
      return usePublicationCollaboratorFeedStore().feed.length > 0;
    },
    domainUrl() {
      if (this.publication.domain) {
        if (this.publication.domain.startsWith("http", 0)) {
          return this.publication.domain;
        }
        return "https://" + this.publication.domain;
      }
      return this.internalDomain;
    },
    internalDomain() {
      return `${window.location.origin}/destination/${this.publicationId}`;
    },
    breadcrumbs() {
      return [
        { url: "/", label: "Home" },
        { url: "/community", label: "Community" },
        { url: "/community/publications", label: "Publications" },
      ];
    },
    dropdownItems() {
      let items = [];
      if (this.ownedNetworks.length > 0) {
        items.push({
          name: "invite_to_network",
          label: "Invite to Network",
          actionType: MenuConstants.ACTION_TYPE_BUTTON_CLICK,
          actionValue: "invite_to_network",
        });
      }
      return items;
    },
  },
  methods: {
    usePublicationNetworksFeedStore,
    useCommunityPublicationStoryFeedStore,
    usePublicationCollaboratorFeedStore,
    ...mapActions(useNetworkStore, ["openInvitePublicationModal"]),
    openDropdown() {
      this.isDropdownOpen = true;
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    updateStatus(status) {
      if (status === "invite_to_network") {
        this.openInvitePublicationModal(this.publicationId);
      }
    },
    goToSubscribeNowPage() {
      window.location.href = `${this.publication.domain}/subscribe-now`;
    },
  },
};
</script>

<!-- eslint-disable-next-line -->
<style lang="scss">
.card-container {
  gap: 48px !important;
}

.story-card-section {
  border-left: 1px solid #ddd !important;
  padding-left: 8px !important;
}

.description {
  max-width: 600px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
